import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const images = [
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/aa4f350c-dd73-4fdd-8d0f-980e49d44d8e.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/c33ca8cf-ce0f-4459-98d2-7858340a7009.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/705ee620-5194-4d63-a496-7307c2897fc0.JPG",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/1f98bfeb-c71e-4649-b30b-d375d6b69f39.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/ec0d9c8c-09b2-4c23-9e48-3169246c49d9.png",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/8cfa7926-7924-49e4-9ab2-cc6e9b028e42.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/6da82b95-e719-4e95-8720-5db080f17f70.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/2cc74b43-92cc-42a8-8f80-fd5d7fe51fbc.JPG",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/35e0c7b6-2128-429f-a64f-fab71a3d2148.JPG",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/3fe30dec-2ce8-486d-94ae-1be39eee0461.png",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/8a3bbf86-9d5b-4ef0-addb-18b5f9f6ab01.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/50a07f3a-c4e7-4fe8-9d58-56a268eb197d.png",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/74c67b8b-8f26-4364-80e2-919211f98b2f.jpeg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/f2101f76-2f0a-4b05-988c-aef03c8419b5.png",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/36bc1888-9191-403f-a0cd-b2a5eeace48f.jpg",
    "https://assets.auxodev.dk/assets/97a23b31-82db-40a5-a298-876516351407/31c3c4a6-4de5-4315-ad39-726b5de0d455.jpeg"
  ];

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Galleri — Toftes Hundesalon</title>
        <meta name="description" content="Se billeder af de glade hunde der har besøgt Toftes Hundesalon. Vores galleri viser resultaterne af vores kærlige pleje." />
        <meta property="og:title" content="Galleri — Toftes Hundesalon" />
      </Helmet>

      {/* Header */}
      <section className="py-24 px-4 bg-stone-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-sm uppercase tracking-[0.4em] font-bold text-stone-700 mb-6">Vores gæster</h2>
          <h1 className="!text-[2rem] md:text-6xl font-serif italic text-stone-900 mb-10 leading-tight">Galleri</h1>
          <p className="text-xl text-gray-600 italic">
            Se et udvalg af de glade hunde, der har besøgt salonen.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((img, idx) => (
              <motion.div
                key={idx}
                layoutId={`image-${idx}`}
                onClick={() => setSelectedImage({ src: img, id: idx })}
                className="relative aspect-[4/5] overflow-hidden cursor-pointer group shadow-sm"
              >
                <img 
                  src={img} 
                  alt={`Galleri ${idx + 1}`} 
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                />
              </motion.div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="mt-24 flex flex-col sm:flex-row justify-center gap-8">
            <a 
              href="https://tofteshundesalon.planway.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-stone-800 text-white px-12 py-5 rounded-none text-xs uppercase tracking-widest font-bold hover:bg-stone-900 transition-all shadow-xl"
            >
              Book en tid nu
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
          >
            <button 
              className="absolute top-6 right-6 text-white hover:text-stone-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-10 h-10" />
            </button>
            <motion.img
              layoutId={`image-${selectedImage.id}`}
              src={selectedImage.src}
              alt="Forstørret billede"
              className="max-w-full max-h-full object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GalleryPage;
